<template>
  <div v-if="showBanner" class="cookie-consent-banner">
    <div class="cookie-content">
      <div class="cookie-icon">
        <ion-icon :icon="shieldCheckmarkOutline"></ion-icon>
      </div>
      
      <div class="cookie-text">
        <h3>{{ t('cookieConsentTitle') }}</h3>
        <p>{{ t('cookieConsentMessage') }}</p>
      </div>

      <div class="cookie-actions">
        <ion-button fill="clear" @click="showSettings = true">
          {{ t('cookieSettings') }}
        </ion-button>
        <ion-button @click="acceptAll" color="primary">
          {{ t('acceptAll') }}
        </ion-button>
      </div>
    </div>

    <!-- Settings Modal -->
    <ion-modal :is-open="showSettings" @did-dismiss="showSettings = false">
      <ion-header>
        <ion-toolbar>
          <ion-title>{{ t('cookieSettings') }}</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="showSettings = false">
              <ion-icon :icon="closeOutline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      
      <ion-content class="ion-padding">
        <p class="cookie-description">{{ t('cookieSettingsDescription') }}</p>

        <ion-list>
          <!-- Necessary Cookies (Always On) -->
          <ion-item>
            <ion-label>
              <h2>{{ t('cookieNecessary') }}</h2>
              <p>{{ t('cookieNecessaryDesc') }}</p>
            </ion-label>
            <ion-toggle :checked="true" disabled></ion-toggle>
          </ion-item>

          <!-- Functional Cookies -->
          <ion-item>
            <ion-label>
              <h2>{{ t('cookieFunctional') }}</h2>
              <p>{{ t('cookieFunctionalDesc') }}</p>
            </ion-label>
            <ion-toggle v-model="preferences.functional"></ion-toggle>
          </ion-item>

          <!-- Analytics Cookies -->
          <ion-item>
            <ion-label>
              <h2>{{ t('cookieAnalytics') }}</h2>
              <p>{{ t('cookieAnalyticsDesc') }}</p>
            </ion-label>
            <ion-toggle v-model="preferences.analytics"></ion-toggle>
          </ion-item>

          <!-- Marketing Cookies -->
          <ion-item>
            <ion-label>
              <h2>{{ t('cookieMarketing') }}</h2>
              <p>{{ t('cookieMarketingDesc') }}</p>
            </ion-label>
            <ion-toggle v-model="preferences.marketing"></ion-toggle>
          </ion-item>
        </ion-list>

        <div class="button-group ion-padding-top">
          <ion-button expand="block" @click="savePreferences">
            {{ t('savePreferences') }}
          </ion-button>
        </div>
      </ion-content>
    </ion-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import {
  IonButton,
  IonIcon,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonToggle
} from '@ionic/vue'
import { shieldCheckmarkOutline, closeOutline } from 'ionicons/icons'
import { privacyService } from '../services/privacy'
import { useI18n } from '../i18n'

const { t } = useI18n()

const showBanner = ref(false)
const showSettings = ref(false)
const preferences = ref({
  functional: true,
  analytics: false,
  marketing: false
})

onMounted(() => {
  // Check if user has already given consent
  if (!privacyService.hasConsent()) {
    showBanner.value = true
  }
})

function acceptAll() {
  privacyService.saveCookieConsent({
    necessary: true,
    functional: true,
    analytics: true,
    marketing: true
  })
  showBanner.value = false
}

function savePreferences() {
  privacyService.saveCookieConsent({
    necessary: true,
    functional: preferences.value.functional,
    analytics: preferences.value.analytics,
    marketing: preferences.value.marketing
  })
  showBanner.value = false
  showSettings.value = false
}
</script>

<style scoped>
.cookie-consent-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--ion-background-color);
  border-top: 1px solid var(--ion-border-color);
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10000;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

.cookie-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.cookie-icon {
  flex-shrink: 0;
}

.cookie-icon ion-icon {
  font-size: 32px;
  color: var(--ion-color-primary);
}

.cookie-text {
  flex: 1;
  min-width: 0;
}

.cookie-text h3 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ion-text-color);
}

.cookie-text p {
  margin: 0;
  font-size: 14px;
  color: var(--ion-color-medium);
}

.cookie-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.cookie-description {
  color: var(--ion-color-medium);
  margin-bottom: 20px;
}

.button-group {
  margin-top: 20px;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .cookie-content {
    flex-direction: column;
    text-align: center;
  }

  .cookie-actions {
    width: 100%;
    flex-direction: column;
  }

  .cookie-actions ion-button {
    width: 100%;
  }
}
</style>
